/**
 * TestDefinitionLinksSection.tsx
 * PR B (qa/test-catalog-modernize-2026-07-11/PLAN.md) — B2: the Links section
 * of the one-object detail pane: the lab-test mapping status (chip + mapped
 * lab test name + "Open Test Mapping" deep link) and the Prerequisites /
 * Methodology buttons that open the EXISTING per-item editors (D6 — their
 * internals are unchanged).
 */

import { Badge, Box, Button, Chip, Stack, Typography, alpha, useTheme } from '@mui/material';
import { AccountTree, Article, OpenInNew } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import type { Theme } from '@mui/material/styles';
import { ROUTES } from '../../../constants/routes';
import type { MappingStatus, TestDefinitionItem } from '../../../services/testDefinitionService';
import { SectionHeader } from './TestDefinitionFormSections';

// ── Mapping status chip (semantics mirror the Test Mapping page) ─────────────

const mappingChipMeta = (status: MappingStatus, theme: Theme) => {
  switch (status) {
    case 'confirmed':
      return { label: 'Confirmed mapping', color: theme.admin.mappingConfirmed, filled: false };
    case 'unconfirmed':
      return { label: 'Unconfirmed mapping', color: theme.admin.mappingUnconfirmed, filled: false };
    case 'name-match':
      return { label: 'Name match', color: theme.admin.mappingNameMatch, filled: false };
    case 'unmapped':
      return { label: 'Unmapped', color: theme.admin.mappingUnconfirmed, filled: true };
  }
};

interface LinksSectionProps {
  item: TestDefinitionItem;
  onOpenPrerequisites: () => void;
  onOpenMethodology: () => void;
}

export const TestDefinitionLinksSection = ({
  item,
  onOpenPrerequisites,
  onOpenMethodology,
}: LinksSectionProps) => {
  const theme = useTheme();
  const meta = mappingChipMeta(item.mappingStatus, theme);

  return (
    <Box>
      <SectionHeader title="Links" />
      <Stack spacing={1.5}>
        {/* Lab-test mapping */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={meta.label}
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 700,
              backgroundColor: alpha(meta.color, meta.filled ? 0.18 : 0.07),
              color: meta.color,
              border: meta.filled ? 'none' : `1px solid ${alpha(meta.color, 0.5)}`,
            }}
          />
          {item.labTestName ? (
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              Lab test: <b>{item.labTestName}</b>
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              No lab test maps here — its work-order lines cannot reach this catalog entry.
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            component={RouterLink}
            to={ROUTES.ADMIN_TEST_MAPPING}
            endIcon={<OpenInNew fontSize="small" />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Open Test Mapping
          </Button>
        </Box>

        {/* Prerequisites + Methodology (existing per-item editors, D6) */}
        <Stack direction="row" spacing={1.5}>
          <Badge color="primary" variant="dot" invisible={!item.hasPrerequisites}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AccountTree />}
              onClick={onOpenPrerequisites}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Prerequisites
            </Button>
          </Badge>
          <Badge color="primary" variant="dot" invisible={!item.hasMethodology}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Article />}
              onClick={onOpenMethodology}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Methodology
            </Button>
          </Badge>
        </Stack>
      </Stack>
    </Box>
  );
};
